import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearSearch, saveSearchTerm, selectSearch, setSearchTerm } from '@actions/search';
import styles from './Search.module.scss';
import { ChevronRightIcon, CircledXIcon, MagnifyingGlassIcon } from '@components/Icons';
import { useHotkeyHandler } from '@support/hooks/use-hotkey-handler';
import { selectNavBarState } from '@reducers/ui/navBar';
import { openModal } from '@actions/ui';

export default function Search() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const search = useSelector(selectSearch);
  const { status } = useSelector(selectNavBarState);
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(search.term);

  const focusInputAndRedirectToSearch = ({
    shouldSelectText,
  }: { shouldSelectText?: boolean } = {}) => {
    if (location.pathname !== '/search') {
      navigate('/search');
    }

    if (shouldSelectText) {
      setInputFocused(true);
      inputRef.current?.select();
    }

    if (!inputFocused) {
      setInputFocused(true);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (['Enter', 'ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
        e.preventDefault();
        inputRef.current?.blur();
      }

      if (e.key == 'Escape') {
        navigate(-1);
      }
    },
    [navigate],
  );

  const handleFocus = useCallback(() => {
    if (!inputFocused) {
      focusInputAndRedirectToSearch();
    }
  }, [inputFocused, focusInputAndRedirectToSearch]);

  const handleBlur = useCallback(() => {
    dispatch(saveSearchTerm(search.term));
    setInputFocused(false);
  }, [search.term]);

  const clearSearchViaBtn = useCallback((e?: SyntheticEvent) => {
    e.preventDefault();
    dispatch(clearSearch());
    inputRef.current?.focus?.();
  }, []);

  useEffect(() => {
    if (
      lastSearchTerm &&
      search.term &&
      location.pathname !== '/search' &&
      !location.pathname.startsWith('/contact')
    ) {
      dispatch(clearSearch());
      inputRef.current?.blur?.();
    }
  }, [search.term, location.pathname]);

  useEffect(() => {
    if (!search.term && location.pathname === '/search') {
      focusInputAndRedirectToSearch();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!lastSearchTerm && !!search.term && location.pathname !== '/search') {
      navigate('/search');
    }

    setLastSearchTerm(search.term);
  }, [search.term, location.pathname, navigate]);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (inputFocused) {
          return;
        }

        if (location.pathname == '/search' && e.key == 'Escape') {
          e.preventDefault();
          focusInputAndRedirectToSearch({ shouldSelectText: true });
        }

        if (e.key == '/') {
          e.preventDefault();

          if (status !== 'pinned') {
            dispatch(openModal('contactSearch'));
            return;
          }

          focusInputAndRedirectToSearch({ shouldSelectText: true });
        }
      },
    },
    [inputFocused, focusInputAndRedirectToSearch, status],
  );

  return (
    <label
      className={cn(
        styles.container,
        inputFocused && styles.focused,
        status !== 'pinned' && styles.floatingNavBar,
      )}
      id="search-input"
    >
      <MagnifyingGlassIcon className={styles.icon} color="textPrimary" />
      <input
        type="text"
        className={styles.input}
        placeholder={inputFocused ? 'Type to search…' : 'Search'}
        ref={inputRef}
        value={search.term}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => dispatch(setSearchTerm(e.target.value))}
        autoFocus={location.pathname == '/search'}
      />
      <a href="#" tabIndex={-1} className={styles.clear} onClick={clearSearchViaBtn}>
        <CircledXIcon stroke="#434648" />
      </a>
      <button
        className={styles.popCommandBarBtn}
        onClick={() => dispatch(openModal('contactSearch'))}
      >
        Search
        <ChevronRightIcon color="textSecondary" width="6" strokeWidth="2" />
      </button>
    </label>
  );
}
